import React, { useState, useMemo } from 'react'
import SearchBar from '../components/SearchBar'
import ToolCard from '../components/ToolCard'
import SEOHead, { seoConfigs } from '../components/SEOHead'
import aiToolsData from '../data/aiTools.json'

const ToolsPage = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [isCategoryExpanded, setIsCategoryExpanded] = useState(true)

  // Get unique categories
  const categories = useMemo(() => {
    const uniqueCategories = [
      ...new Set(aiToolsData.map((tool) => tool.category)),
    ]
    return ['All', ...uniqueCategories.sort()]
  }, [])

  // Filter tools based on search term and category
  const filteredTools = useMemo(() => {
    return aiToolsData.filter((tool) => {
      const matchesSearch =
        searchTerm === '' ||
        tool.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
        tool.features.some((feature) =>
          feature.toLowerCase().includes(searchTerm.toLowerCase())
        )

      const matchesCategory =
        selectedCategory === 'All' || tool.category === selectedCategory

      return matchesSearch && matchesCategory
    })
  }, [searchTerm, selectedCategory])

  // Group tools by category for display
  const groupedTools = useMemo(() => {
    if (selectedCategory !== 'All') {
      return { [selectedCategory]: filteredTools }
    }

    return filteredTools.reduce((groups, tool) => {
      const category = tool.category
      if (!groups[category]) {
        groups[category] = []
      }
      groups[category].push(tool)
      return groups
    }, {})
  }, [filteredTools, selectedCategory])

  return (
    <>
      <SEOHead {...seoConfigs.tools} />
      <div className='space-y-8'>
        {/* Header */}
        <div className='text-center space-y-4'>
          <h1 className='text-4xl md:text-6xl font-bold text-white mb-4'>
            AI Tools Directory
          </h1>
          <p className='text-xl text-white/80 max-w-3xl mx-auto'>
            Explore the leading AI-driven platforms!
          </p>
          <div className='flex items-center justify-center space-x-4 text-white/60'>
            <div className='flex items-center space-x-2'>
              <div className='w-2 h-2 bg-blue-500 rounded-full animate-pulse'></div>
              <span>{aiToolsData.length} Tools</span>
            </div>
            <div className='flex items-center space-x-2'>
              <div className='w-2 h-2 bg-green-500 rounded-full animate-pulse'></div>
              <span>{categories.length - 1} Categories</span>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <SearchBar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          placeholder='Search AI tools by name, category, or features...'
        />

        {/* Category Filter */}
        <div className='glass-card rounded-2xl p-4 sm:p-6'>
          <button
            onClick={() => setIsCategoryExpanded(!isCategoryExpanded)}
            className='w-full flex items-center justify-between text-white font-semibold mb-4 focus:outline-none focus:ring-2 focus:ring-white/50 rounded-lg p-2 hover:bg-white/10 transition-all duration-300'
            aria-expanded={isCategoryExpanded}
            aria-controls='category-filter-content'
          >
            <span className='flex items-center space-x-2'>
              <svg
                className='w-5 h-5'
                fill='none'
                viewBox='0 0 24 24'
                stroke='currentColor'
                aria-hidden='true'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  strokeWidth={2}
                  d='M19 11H5m14-7l2 2-2 2M5 6l2-2-2-2m0 16l2-2-2-2m14 2l2-2-2-2'
                />
              </svg>
              <span>Filter by Category</span>
              {selectedCategory !== 'All' && (
                <span className='bg-blue-500 text-white text-xs px-2 py-1 rounded-full'>
                  {selectedCategory}
                </span>
              )}
            </span>
            <svg
              className={`w-5 h-5 transition-transform duration-300 ${
                isCategoryExpanded ? 'rotate-180' : ''
              }`}
              fill='none'
              viewBox='0 0 24 24'
              stroke='currentColor'
              aria-hidden='true'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M19 9l-7 7-7-7'
              />
            </svg>
          </button>

          <div
            id='category-filter-content'
            className={`transition-all duration-300 overflow-hidden ${
              isCategoryExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className='max-h-64 sm:max-h-none overflow-y-auto'>
              <div className='flex flex-wrap gap-2 pr-2'>
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => {
                      setSelectedCategory(category)
                      // Auto-collapse on mobile after selection
                      if (window.innerWidth < 640) {
                        setIsCategoryExpanded(false)
                      }
                    }}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-white/50 ${
                      selectedCategory === category
                        ? 'bg-blue-500 text-white shadow-lg'
                        : 'bg-white/10 text-white/80 hover:bg-white/20 hover:text-white'
                    }`}
                  >
                    {category}
                    {category !== 'All' && (
                      <span className='ml-2 text-xs opacity-75'>
                        (
                        {
                          aiToolsData.filter(
                            (tool) => tool.category === category
                          ).length
                        }
                        )
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Results Count */}
        {searchTerm && (
          <div className='text-center'>
            <p className='text-white/80'>
              Found {filteredTools.length} tool
              {filteredTools.length !== 1 ? 's' : ''}
              {searchTerm && ` matching "${searchTerm}"`}
            </p>
          </div>
        )}

        {/* Tools Grid by Category */}
        <div className='space-y-12'>
          {Object.entries(groupedTools).map(([category, tools]) => (
            <div key={category} className='space-y-6'>
              <div className='flex items-center space-x-4'>
                <h2 className='text-2xl font-bold text-white'>{category}</h2>
                <div className='flex-1 h-px bg-gradient-to-r from-white/20 to-transparent'></div>
                <span className='text-white/60 text-sm'>
                  {tools.length} tools
                </span>
              </div>

              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'>
                {tools.map((tool, index) => (
                  <div
                    key={tool.id}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <ToolCard tool={tool} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* No Results */}
        {filteredTools.length === 0 && (
          <div className='text-center py-12'>
            <div className='glass-card rounded-2xl p-8 max-w-md mx-auto'>
              <div className='w-16 h-16 bg-gradient-to-r from-gray-500 to-gray-600 rounded-full flex items-center justify-center mx-auto mb-4'>
                <svg
                  className='w-8 h-8 text-white'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
              </div>
              <h3 className='text-xl font-semibold text-white mb-2'>
                No tools found
              </h3>
              <p className='text-white/70 mb-4'>
                Try adjusting your search terms or category filter.
              </p>
              <button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedCategory('All')
                }}
                className='px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors'
              >
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default ToolsPage
